import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  Chip,
  Stack,
} from '@mui/material';
import {
  ColorLens as ColorIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useBrand } from './ServerBrandProvider';
import { useNotify } from 'react-admin';

interface ColorInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
}

const ColorInput: React.FC<ColorInputProps> = ({ label, value, onChange, description }) => (
  <Box sx={{ mb: 2 }}>
    <InputLabel sx={{ mb: 1, fontWeight: 'bold' }}>{label}</InputLabel>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          backgroundColor: value,
          border: '2px solid #ddd',
          borderRadius: 1,
          cursor: 'pointer',
        }}
        onClick={() => {
          const input = document.createElement('input');
          input.type = 'color';
          input.value = value;
          input.onchange = (e) => onChange((e.target as HTMLInputElement).value);
          input.click();
        }}
      />
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        size="small"
        sx={{ minWidth: 120 }}
      />
      <Box sx={{ flex: 1 }}>
        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  </Box>
);

interface GradientInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
}

const GradientInput: React.FC<GradientInputProps> = ({ label, value, onChange, description }) => (
  <Box sx={{ mb: 2 }}>
    <InputLabel sx={{ mb: 1, fontWeight: 'bold' }}>{label}</InputLabel>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          background: value,
          border: '2px solid #ddd',
          borderRadius: 1,
        }}
      />
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="linear-gradient(...)"
        size="small"
        sx={{ flex: 1 }}
      />
    </Box>
    {description && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        {description}
      </Typography>
    )}
  </Box>
);

export const BrandingSettings: React.FC = () => {
  const { brand, loading, error, updateBranding, refreshBranding } = useBrand();
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState('colors');
  const [saving, setSaving] = useState(false);
  const [localChanges, setLocalChanges] = useState<any>({});

  const hasUnsavedChanges = Object.keys(localChanges).length > 0;

  const handleSave = async () => {
    if (!hasUnsavedChanges) {
      return;
    }

    try {
      setSaving(true);
      await updateBranding(localChanges);
      setLocalChanges({});
      notify('Branding settings saved successfully!', { type: 'success' });
    } catch (err) {
      notify('Failed to save branding settings', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLocalChanges({});
    refreshBranding();
  };

  const updateLocalValue = (path: string, value: any) => {
    const keys = path.split('.');
    const newChanges = { ...localChanges };
    
    let current = newChanges;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    
    setLocalChanges(newChanges);
  };

  const getValue = (path: string) => {
    const keys = path.split('.');
    let current: any = localChanges;
    
    for (const key of keys) {
      if (current && current[key] !== undefined) {
        current = current[key];
      } else {
        // Fall back to brand config
        current = brand;
        for (const brandKey of keys) {
          current = current?.[brandKey];
        }
        break;
      }
    }
    
    return current;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!brand) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load branding configuration. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaletteIcon color="primary" />
              Branding Settings
            </Typography>
            
            <Stack direction="row" spacing={2}>
              {hasUnsavedChanges && (
                <Chip 
                  label="Unsaved Changes" 
                  color="warning" 
                  size="small" 
                />
              )}
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                disabled={saving || !hasUnsavedChanges}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || !hasUnsavedChanges}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </Stack>
          </Box>

          {error && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 3 }}>
            <Tab label="Colors" value="colors" icon={<ColorIcon />} />
            <Tab label="Logo & Assets" value="assets" icon={<ImageIcon />} />
          </Tabs>

          {activeTab === 'colors' && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ColorIcon />
                Color Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Customize your brand colors. Changes will be applied immediately to the preview.
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Brand Colors
                    </Typography>
                    
                    <ColorInput
                      label="Primary Color"
                      value={getValue('colors.primary')}
                      onChange={(value) => updateLocalValue('colors.primary', value)}
                      description="Main brand color used for headers, buttons, and links"
                    />
                    
                    <ColorInput
                      label="Secondary Color"
                      value={getValue('colors.secondary')}
                      onChange={(value) => updateLocalValue('colors.secondary', value)}
                      description="Secondary accent color for highlights and variations"
                    />
                    
                    <ColorInput
                      label="Accent Color"
                      value={getValue('colors.accent')}
                      onChange={(value) => updateLocalValue('colors.accent', value)}
                      description="Accent color for special elements and calls-to-action"
                    />
                    
                    <ColorInput
                      label="Background Color"
                      value={getValue('colors.background')}
                      onChange={(value) => updateLocalValue('colors.background', value)}
                      description="Main background color for the application"
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Gradients & Effects
                    </Typography>
                    
                    <GradientInput
                      label="Header Gradient"
                      value={getValue('theme.headerGradient')}
                      onChange={(value) => updateLocalValue('theme.headerGradient', value)}
                      description="Gradient used for page headers and navigation"
                    />
                    
                    <GradientInput
                      label="Button Gradient"
                      value={getValue('theme.buttonGradient')}
                      onChange={(value) => updateLocalValue('theme.buttonGradient', value)}
                      description="Gradient used for primary buttons"
                    />
                    
                    <GradientInput
                      label="Card Gradient (Optional)"
                      value={getValue('theme.cardGradient') || ''}
                      onChange={(value) => updateLocalValue('theme.cardGradient', value)}
                      description="Optional gradient for special cards and panels"
                    />
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Live Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Preview how your colors will look in the application
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      width: 100,
                      height: 60,
                      background: getValue('theme.headerGradient'),
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                    }}
                  >
                    Header
                  </Box>
                  <Box
                    sx={{
                      width: 100,
                      height: 60,
                      background: getValue('theme.buttonGradient'),
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                    }}
                  >
                    Button
                  </Box>
                  <Box
                    sx={{
                      width: 100,
                      height: 60,
                      backgroundColor: getValue('colors.primary'),
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                    }}
                  >
                    Primary
                  </Box>
                  <Box
                    sx={{
                      width: 100,
                      height: 60,
                      backgroundColor: getValue('colors.secondary'),
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                    }}
                  >
                    Secondary
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {activeTab === 'assets' && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon />
                Logo & Assets
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage your brand logo and other visual assets.
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Brand Information
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="Brand Name"
                      value={getValue('brandName')}
                      onChange={(e) => updateLocalValue('brandName', e.target.value)}
                      sx={{ mb: 2 }}
                      helperText="This will appear in the page title and navigation"
                    />
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Font Family</InputLabel>
                      <Select
                        value={getValue('fontFamily')}
                        onChange={(e) => updateLocalValue('fontFamily', e.target.value)}
                        label="Font Family"
                      >
                        <MenuItem value="Roboto">Roboto</MenuItem>
                        <MenuItem value="Arial, sans-serif">Arial</MenuItem>
                        <MenuItem value="Helvetica, sans-serif">Helvetica</MenuItem>
                        <MenuItem value="'Times New Roman', serif">Times New Roman</MenuItem>
                        <MenuItem value="Georgia, serif">Georgia</MenuItem>
                        <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
                      </Select>
                    </FormControl>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Current Logo
                    </Typography>
                    
                    {getValue('logoUrl') ? (
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            border: '2px dashed #ddd',
                            borderRadius: 1,
                            p: 2,
                            textAlign: 'center',
                            backgroundColor: '#f9f9f9',
                          }}
                        >
                          <img
                            src={getValue('logoUrl')}
                            alt="Current Logo"
                            style={{
                              maxWidth: '200px',
                              maxHeight: '80px',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          Current logo: {getValue('logoUrl')}
                        </Typography>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No logo configured. Please contact your administrator to upload a logo.
                      </Alert>
                    )}

                    <TextField
                      fullWidth
                      label="Logo URL"
                      value={getValue('logoUrl') || ''}
                      onChange={(e) => updateLocalValue('logoUrl', e.target.value)}
                      helperText="URL to your logo image file"
                      size="small"
                    />
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};